// Budget byte e truncation dei payload prima dell'invio (CYJS-10).
//
// I limiti del servizio (contratto condiviso, CYRA-110/112) rifiutano con `413`
// i payload troppo grandi. Senza un budget applicato lato client, un evento con
// breadcrumb/contexts enormi, un batch di log o un chunk di replay grande
// verrebbe perso INTERAMENTE: proprio il segnale che serviva. Qui riduciamo
// PRIMA i contenuti meno importanti — preservando identità ed errore principale —
// e marchiamo il payload con `truncated: true`.
//
// Misuriamo BYTE UTF-8 dopo `JSON.stringify`, NON caratteri: un emoji è 1 code
// point ma 4 byte, ed è la dimensione in byte quella che il server confronta col
// limite. Applicare il budget a monte evita anche che un gateway trusted rifiuti
// (413) e ritenti un payload che non rientrerebbe mai (CYRA-112).
//
// Strategia per canale (verificata ai confini X-1/X/X+1 nei test):
//   - eventi (error/message) → capEvent: droppa in quest'ordine breadcrumbs (i
//     più vecchi), request, contexts custom (tiene runtime/os/app/device/replay),
//     extra, tags, frames dello stacktrace; come ultima risorsa tronca il value
//     dell'errore. Non tocca mai event_id/level/timestamp/exception.type.
//   - log (batch) → chunkLogBatch: tronca il singolo log troppo grande
//     (attributes poi message) e spezza l'array in più richieste ciascuna sotto
//     budget e sotto il cap di conteggio, senza perdere alcun log.
//   - replay (chunk) → capReplayChunk: droppa gli eventi rrweb dalla coda tenendo
//     il prefisso in ordine; identità di sessione (replay_session_id/seq) intatta.
//   - metriche/pageview → capGeneric: campi già piccoli a monte, ma label/url/
//     route/path/referrer/utm (e le iniezioni di `beforeSend`) non hanno un tetto
//     proprio, quindi una rete generica li tronca/rimuove se serve.
//
// Ogni cap chiude con la rete di sicurezza `enforceBudget`, che GARANTISCE il
// rientro sotto budget sfoltendo i residui non identitari (anche quelli iniettati
// da `beforeSend`) che la strategia specifica non ha previsto.

import type { EventPayload } from './types'

/// Budget conservativo per singola richiesta HTTP, in byte UTF-8 del body JSON
/// (NON compresso — il transport non gzippa). Derivato dal contratto (API.md §6):
/// envelope decompresso max 5 MB (`MAX_DECOMPRESSED`), compresso max 1 MB
/// (`MAX_COMPRESSED`); un gateway trusted applica un `DefaultMaxBodyBytes`
/// «almeno altrettanto restrittivo». 512 KiB sta ampiamente sotto tutti: un
/// evento/chunk sano non lo sfiora, la truncation scatta solo su payload anomali.
export const MAX_PAYLOAD_BYTES = 512 * 1024

// Overhead in byte delle parentesi quadre di un array JSON (`[` + `]`). Serve a
// capare il singolo elemento lasciando spazio al wrapper del suo chunk.
const ARRAY_BRACKETS = 2

// Contexts identitari/diagnostici da preservare quando si sfoltiscono i contexts
// custom: legano l'evento (replay) o descrivono l'ambiente (parità col contratto,
// dove os/app sono indicizzati). Tutto il resto è "custom" e cade per primo.
const CORE_CONTEXTS = new Set(['runtime', 'os', 'app', 'device', 'replay'])

// Chiavi identitarie per canale: la rete di sicurezza `enforceBudget` non le
// rimuove mai (e non tronca le loro stringhe). Ciò che resta fuori è riducibile.
const EVENT_KEEP = new Set([
  'event_id',
  'timestamp',
  'platform',
  'level',
  'environment',
  'release',
  'server_name',
  'sdk',
  'trace_id',
  'exception',
  'message',
  'contexts',
  'user',
  'truncated',
])
const LOG_KEEP = new Set([
  'event_id',
  'timestamp',
  'level',
  'message',
  'trace_id',
  'sdk',
  'truncated',
])
const REPLAY_KEEP = new Set([
  'replay_session_id',
  'seq',
  'started_at',
  'events',
  'environment',
  'truncated',
])
// Canali a forma libera (metriche/pageview): l'identità minima comune. Label,
// url, route, path, referrer, utm sono stringhe fuori dal set → troncate se
// serve; gli oggetti custom (o iniettati da beforeSend) vengono rimossi.
const GENERIC_KEEP = new Set([
  'kind',
  'subtype',
  'name',
  'event_id',
  'environment',
  'trace_id',
  'timestamp',
  'truncated',
])

// Encoder condiviso (browser + Node ≥ 20): `TextEncoder().encode` conta i byte
// UTF-8 reali. Cache lazy alla prima chiamata; assente solo in ambienti minimali.
let cachedEncoder: TextEncoder | undefined

/// Byte UTF-8 di [value] (non caratteri): la metrica che il server confronta.
export function utf8Bytes(value: string): number {
  if (typeof TextEncoder === 'function') {
    cachedEncoder ??= new TextEncoder()
    return cachedEncoder.encode(value).length
  }
  return manualUtf8Bytes(value)
}

// Fallback senza `TextEncoder`: somma i byte per code point (`for…of` itera per
// code point, quindi i surrogate pair contano 4 byte una volta sola).
function manualUtf8Bytes(value: string): number {
  let bytes = 0
  for (const ch of value) {
    const cp = ch.codePointAt(0) ?? 0
    if (cp <= 0x7f) bytes += 1
    else if (cp <= 0x7ff) bytes += 2
    else if (cp <= 0xffff) bytes += 3
    else bytes += 4
  }
  return bytes
}

/// Byte UTF-8 del payload serializzato in JSON.
export function payloadBytes(payload: unknown): number {
  return utf8Bytes(JSON.stringify(payload) ?? '')
}

// Byte del contributo JSON di una stringa: `JSON.stringify` la avvolge tra
// virgolette ed ESCAPA `"`, `\` e i caratteri di controllo (`"`→`\"`,
// newline→`\n`, ctrl→`\u00XX`), quindi la stringa serializzata può pesare più di
// quella grezza. Contiamo i byte reali che finiscono nel body, meno le 2
// virgolette esterne, così il budget del campo è calcolato «dopo JSON.stringify».
function jsonEscapedBytes(str: string): number {
  return utf8Bytes(JSON.stringify(str)) - 2
}

/// Tronca [str] così che il suo contributo JSON (byte dopo l'escaping) sia ≤
/// [maxEscapedBytes], senza spezzare un code point (i surrogate pair restano
/// interi). È la troncatura giusta per un campo che verrà serializzato in JSON.
export function truncateUtf8Json(str: string, maxEscapedBytes: number): string {
  if (maxEscapedBytes <= 0) return ''
  if (jsonEscapedBytes(str) <= maxEscapedBytes) return str
  let out = ''
  let used = 0
  for (const ch of str) {
    const cost = utf8Bytes(JSON.stringify(ch)) - 2
    if (used + cost > maxEscapedBytes) break
    out += ch
    used += cost
  }
  return out
}

// Rete di sicurezza comune a tutti i canali: garantisce che, dopo la strategia
// specifica, `payloadBytes(payload) ≤ budget` anche quando restano residui
// riducibili non previsti dalla strategia — campi custom o iniettati da
// `beforeSend`, `logger`/`release`/`path`/`user` sovradimensionati. Prima tronca
// i valori stringa top-level non identitari (il più grande per primo,
// rispettando l'escaping JSON), poi rimuove i valori non-stringa non identitari
// (oggetti/array custom). Le chiavi in [keep] non vengono mai rimosse e le loro
// stringhe non troncate: sono l'identità del canale. Con budget realistico basta
// sempre; sotto budget minuscoli l'identità irriducibile ha la precedenza.
function enforceBudget(payload: EventPayload, budget: number, keep: Set<string>): void {
  while (payloadBytes(payload) > budget) {
    const key = largestTopLevel(payload, keep, 'string')
    if (!key) break
    const current = payload[key] as string
    const room = budget - payloadBytes({ ...payload, [key]: '' })
    const next = truncateUtf8Json(current, Math.max(0, room))
    if (next === current) break
    payload[key] = next
  }
  while (payloadBytes(payload) > budget) {
    const key = largestTopLevel(payload, keep, 'other')
    if (!key) break
    delete payload[key]
  }
}

// Chiave top-level (non in [keep]) col valore più pesante del tipo richiesto
// (`string` o `other`), o `undefined` se non ce ne sono.
function largestTopLevel(
  payload: EventPayload,
  keep: Set<string>,
  kind: 'string' | 'other',
): string | undefined {
  let best: string | undefined
  let bestBytes = 0
  for (const key of Object.keys(payload)) {
    if (keep.has(key)) continue
    const value = payload[key]
    const isString = typeof value === 'string'
    if (kind === 'string' ? !isString : isString) continue
    const bytes = payloadBytes(value)
    if (bytes > bestBytes) {
      bestBytes = bytes
      best = key
    }
  }
  return best
}

// Clone JSON-safe: i payload sono già JSON (vanno sul filo), così non mutiamo
// l'input dei chiamanti mentre sfoltiamo i rami annidati.
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/// Riduce un evento error/message finché rientra in [budget] byte, preservando
/// identità ed errore principale. Ritorna il payload originale se già sotto
/// budget; altrimenti una copia ridotta con `truncated: true`.
export function capEvent(input: EventPayload, budget: number): EventPayload {
  if (payloadBytes(input) <= budget) return input

  const payload = clone(input)
  // Il flag entra subito: così è già contato in tutte le misure successive e la
  // sua aggiunta non può far risuperare il budget.
  payload['truncated'] = true
  const fits = (): boolean => payloadBytes(payload) <= budget

  // 1. Breadcrumbs: droppa i più vecchi (i meno rilevanti al crash) uno a uno;
  //    se non basta neppure svuotandoli, rimuove la chiave.
  dropOldestBreadcrumbs(payload, fits)
  if (fits()) return payload

  // 2. Request context (URL/headers/body della richiesta): utile ma non identità.
  if (removeKey(payload, 'request') && fits()) return payload

  // 3. Contexts custom: tiene solo i core (runtime/os/app/device/replay).
  if (pruneContexts(payload) && fits()) return payload

  // 4-5. Extra e tags: metadati arricchiti, non spostano il grouping.
  if (removeKey(payload, 'extra') && fits()) return payload
  if (removeKey(payload, 'tags') && fits()) return payload

  // 6. Frames dello stacktrace: tiene i più vicini al crash (in coda), scarta i
  //    più esterni finché rientra.
  if (trimStackFrames(payload, fits)) return payload

  // 7. Ultima risorsa: tronca i value degli errori (e il message formattato)
  //    lasciando type/mechanism. L'identità e la classe d'errore restano.
  truncatePrimaryMessage(payload, budget)
  // 8. Rete di sicurezza: residui top-level non identitari (campi iniettati da
  //    beforeSend) che le 1-7 non toccano.
  enforceBudget(payload, budget, EVENT_KEEP)
  return payload
}

/// Riduce un singolo log finché rientra in [budget]: prima rimuove gli
/// `attributes`, poi tronca il `message`, infine (rete di sicurezza) sfoltisce i
/// campi non identitari residui. Preserva event_id/level/timestamp.
export function capLogEvent(input: EventPayload, budget: number): EventPayload {
  if (payloadBytes(input) <= budget) return input

  const payload = clone(input)
  payload['truncated'] = true
  if (removeKey(payload, 'attributes') && payloadBytes(payload) <= budget)
    return payload

  if (typeof payload['message'] === 'string') {
    // Spazio residuo per il contributo JSON del message = budget meno tutto il
    // resto già serializzato (le virgolette del campo sono già in withoutMessage).
    const withoutMessage = { ...payload, message: '' }
    const room = budget - payloadBytes(withoutMessage)
    payload['message'] = truncateUtf8Json(payload['message'], Math.max(0, room))
  }
  // Campi non identitari residui (logger/release/environment o iniettati da
  // beforeSend) ancora sopra budget: la rete di sicurezza li tronca/rimuove.
  enforceBudget(payload, budget, LOG_KEEP)
  return payload
}

/// Spezza un batch di log in più richieste, ciascuna sotto [budget] byte e sotto
/// [maxCount] elementi, senza perdere alcun log. I singoli log troppo grandi
/// vengono prima troncati da [capLogEvent]. L'array vuoto dà nessun chunk.
export function chunkLogBatch(
  batch: EventPayload[],
  budget: number,
  maxCount: number,
): EventPayload[][] {
  const chunks: EventPayload[][] = []
  let current: EventPayload[] = []
  let currentBytes = ARRAY_BRACKETS

  const push = (): void => {
    if (current.length > 0) chunks.push(current)
    current = []
    currentBytes = ARRAY_BRACKETS
  }

  for (const raw of batch) {
    // Ogni log deve stare da solo in un chunk: lo capiamo a budget meno i bracket.
    const log = capLogEvent(raw, budget - ARRAY_BRACKETS)
    // Byte aggiunti al chunk: l'elemento + la virgola se non è il primo. JSON di
    // un array è `[` + join(',') + `]`, quindi il conteggio è esatto.
    const add = payloadBytes(log) + (current.length > 0 ? 1 : 0)
    const overBytes = currentBytes + add > budget
    const overCount = current.length >= maxCount
    if (current.length > 0 && (overBytes || overCount)) push()
    current.push(log)
    currentBytes += current.length > 1 ? payloadBytes(log) + 1 : payloadBytes(log)
  }
  push()
  return chunks
}

/// Riduce un chunk di replay finché rientra in [budget]: droppa gli eventi rrweb
/// dalla coda tenendo il prefisso in ordine (timeline continua dall'inizio).
/// Identità di sessione (replay_session_id/seq/started_at) sempre preservata.
export function capReplayChunk(input: EventPayload, budget: number): EventPayload {
  if (payloadBytes(input) <= budget) return input

  const payload = clone(input)
  payload['truncated'] = true
  const events = Array.isArray(payload['events'])
    ? (payload['events'] as unknown[])
    : []

  // Prefisso più lungo di eventi che rientra: overhead (chunk con events:[]) +
  // byte cumulati degli eventi + virgole. Il conteggio segue la forma JSON.
  const overhead = payloadBytes({ ...payload, events: [] }) - ARRAY_BRACKETS
  let used = overhead + ARRAY_BRACKETS
  const kept: unknown[] = []
  for (const ev of events) {
    const add = payloadBytes(ev) + (kept.length > 0 ? 1 : 0)
    if (used + add > budget) break
    kept.push(ev)
    used += add
  }
  payload['events'] = kept
  // Rete di sicurezza: se persino i metadati (path/user grandi, campi iniettati)
  // sfondano il budget, sfoltiscili tenendo l'identità di sessione e gli eventi.
  enforceBudget(payload, budget, REPLAY_KEEP)
  return payload
}

/// Cap generico per i canali a forma libera (metriche, pageview): se supera
/// [budget], tronca i valori stringa non identitari (label/url/route/path/
/// referrer/utm…) e rimuove i valori custom, marcando `truncated`. L'identità
/// minima del canale (kind/subtype/name/environment…) resta.
export function capGeneric(input: EventPayload, budget: number): EventPayload {
  if (payloadBytes(input) <= budget) return input
  const payload = clone(input)
  payload['truncated'] = true
  enforceBudget(payload, budget, GENERIC_KEEP)
  return payload
}

// ---------------------------------------------------------------- riduttori

// Droppa i breadcrumb più vecchi (testa dell'array) uno a uno finché [fits] o
// l'array è vuoto; in tal caso rimuove del tutto la chiave.
function dropOldestBreadcrumbs(payload: EventPayload, fits: () => boolean): void {
  const container = payload['breadcrumbs']
  const values =
    container &&
    typeof container === 'object' &&
    Array.isArray((container as { values?: unknown }).values)
      ? (container as { values: unknown[] }).values
      : undefined
  if (!values) return
  while (values.length > 0 && !fits()) values.shift()
  if (values.length === 0) delete payload['breadcrumbs']
}

// Rimuove [key] se presente. Ritorna `true` se ha rimosso qualcosa.
function removeKey(payload: EventPayload, key: string): boolean {
  if (!(key in payload)) return false
  delete payload[key]
  return true
}

// Tiene nei contexts solo le chiavi core; rimuove le custom. Ritorna `true` se ha
// tolto almeno una chiave.
function pruneContexts(payload: EventPayload): boolean {
  const contexts = payload['contexts']
  if (!contexts || typeof contexts !== 'object') return false
  const map = contexts as Record<string, unknown>
  let changed = false
  for (const key of Object.keys(map)) {
    if (!CORE_CONTEXTS.has(key)) {
      delete map[key]
      changed = true
    }
  }
  if (Object.keys(map).length === 0) delete payload['contexts']
  return changed
}

// Riduce i frame dello stacktrace tenendo i più vicini al crash (coda dell'array,
// dove `parseStack` mette il frame che ha lanciato) finché [fits]. Ritorna `true`
// se dopo la riduzione il payload rientra.
function trimStackFrames(payload: EventPayload, fits: () => boolean): boolean {
  const values = exceptionValues(payload)
  if (!values) return false
  for (const value of values) {
    const frames = stackFrames(value)
    if (!frames) continue
    while (frames.length > 0 && !fits()) frames.shift()
  }
  return fits()
}

// Tronca i messaggi degli errori (exception.values[*].value e message.formatted)
// all'ultimo respiro, lasciando spazio all'identità già serializzata. Il backend
// legge l'ultimo value, ma anche gli altri occupano byte: li riduce tutti. type e
// mechanism restano intatti. Il conteggio tiene conto dell'escaping JSON.
function truncatePrimaryMessage(payload: EventPayload, budget: number): void {
  const values = exceptionValues(payload)
  if (values) {
    for (let i = 0; i < values.length; i++) {
      const value = values[i]
      if (!value || typeof value.value !== 'string') continue
      const probe = clone(payload)
      const probeValue = exceptionValues(probe)?.[i]
      if (!probeValue) continue
      probeValue.value = ''
      const room = budget - payloadBytes(probe)
      value.value = truncateUtf8Json(value.value, Math.max(0, room))
    }
  }
  const message = payload['message']
  if (
    message &&
    typeof message === 'object' &&
    typeof (message as { formatted?: unknown }).formatted === 'string'
  ) {
    const probe = clone(payload)
    ;(probe['message'] as { formatted: string }).formatted = ''
    const room = budget - payloadBytes(probe)
    ;(message as { formatted: string }).formatted = truncateUtf8Json(
      (message as { formatted: string }).formatted,
      Math.max(0, room),
    )
  }
}

interface ExceptionValue {
  type?: string
  value?: string
  stacktrace?: { frames?: unknown[] }
}

function exceptionValues(payload: EventPayload): ExceptionValue[] | undefined {
  const exception = payload['exception']
  if (!exception || typeof exception !== 'object') return undefined
  const values = (exception as { values?: unknown }).values
  return Array.isArray(values) ? (values as ExceptionValue[]) : undefined
}

function stackFrames(value: ExceptionValue): unknown[] | undefined {
  const frames = value.stacktrace?.frames
  return Array.isArray(frames) ? frames : undefined
}
